import type { LucideIcon } from "lucide-react";
import RenderImage from "@/components/atoms/RenderImage";
import CardAvatar from "@/components/cardElements/CardAvatar";
import { cn } from "@/lib/utils";
type Size = "sm" | "md" | "lg" | "xl";
const SIZES: Record<Size, {
  tile: string;
  overlay: string;
  icon: string;
  dot: number;
}> = {
  sm: {
    tile: "h-8 w-8",
    overlay: "h-5 w-5 border",
    icon: "h-4 w-4",
    dot: 2
  },
  md: {
    tile: "h-10 w-10",
    overlay: "h-5 w-5 border-2",
    icon: "h-5 w-5",
    dot: 2
  },
  lg: {
    tile: "h-12 w-12",
    overlay: "h-6 w-6 border-2",
    icon: "h-6 w-6",
    dot: 3
  },
  xl: {
    tile: "h-16 w-16",
    overlay: "h-7 w-7 border-2",
    icon: "h-7 w-7",
    dot: 3
  }
};
type Person = {
  imageUrl?: string | null;
  name?: string;
  online?: boolean;
};
type Resource = {
  imageUrl?: string | null;
  title?: string;
};
type EntityAvatarProps = {
  imageUrl?: string | null;
  imageAlt?: string;
  hasSubject?: boolean;
  icon?: LucideIcon;
  iconClassName?: string;
  person?: Person;
  resource?: Resource;
  size?: Size;
  className?: string;
};
export function EntityAvatar({
  imageUrl,
  imageAlt,
  hasSubject: hasSubjectProp,
  icon: Icon,
  iconClassName,
  person,
  resource,
  size = "md",
  className
}: EntityAvatarProps) {
  const s = SIZES[size];
  const hasSubject = !!imageUrl || !!Icon || !!hasSubjectProp;
  const overlay = person ? <div className="absolute -bottom-1 -right-1">
      <CardAvatar className={cn("border-card", s.overlay)} imgUrl={person.imageUrl ?? ""} name={person.name} isOnline={person.online} isOnlineSize={s.dot} />
    </div> : resource ? <div title={resource.title} className={cn("absolute -bottom-1 -right-1 overflow-hidden rounded-md border-card bg-card", s.overlay)}>
      <RenderImage className="h-full w-full rounded-[inherit] object-cover" src={resource.imageUrl ?? undefined} alt={resource.title ?? ""} />
    </div> : null;
  if (!hasSubject) {
    return <CardAvatar className={cn(s.tile, className)} imgUrl={person?.imageUrl ?? ""} name={person?.name} isOnline={person?.online} isOnlineSize={s.dot} />;
  }
  return <div className={cn("relative shrink-0 rounded-lg", !Icon && "border border-muted-foreground/30", s.tile, className)} data-sentry-component="EntityAvatar" data-sentry-source-file="EntityAvatar.tsx">
      {!imageUrl && Icon ? <div className={cn("flex h-full w-full items-center justify-center rounded-lg", iconClassName ?? "bg-muted text-muted-foreground")}>
          <Icon className={s.icon} />
        </div> : <RenderImage className="rounded-lg object-cover" src={imageUrl ?? undefined} alt={imageAlt ?? person?.name ?? ""} />}
      {overlay}
    </div>;
}